const SKIP_TYPES = new Set(['hidden', 'checkbox', 'radio', 'button', 'submit', 'file', 'range']);
const PORTAL_SELECTOR = '.ant-modal, .ant-drawer, .ant-select-dropdown, .ant-picker-dropdown, .ant-popover';
const SELECT_SEARCH_CLASS = 'ant-select-selection-search-input';
const SKIP_FLAG = '__skipEnterTab';

const isSkippable = (el: HTMLElement): boolean => {
  if (el.classList.contains(SELECT_SEARCH_CLASS)) {
    return el instanceof HTMLInputElement && el.disabled;
  }
  if (el instanceof HTMLButtonElement) return el.disabled;
  if (el instanceof HTMLInputElement && (SKIP_TYPES.has(el.type) || el.readOnly || el.disabled)) return true;
  if (el instanceof HTMLTextAreaElement && (el.readOnly || el.disabled)) return true;
  return false;
};

// Быстрые кнопки услуг (замена масла, rev. №56) участвуют в навигации как поля:
// Enter на кнопке — штатный click (подтверждение), стрелки — переход между полями
const QUICK_BTN_SELECTOR = 'button.quick-service-btn';

const collectFields = () =>
  Array.from(document.querySelectorAll<HTMLElement>(`input, textarea, ${QUICK_BTN_SELECTOR}`)).filter(
    (el) => el.getClientRects().length > 0 && !isSkippable(el) && !el.closest(PORTAL_SELECTOR),
  );

const focusField = (el: HTMLElement | undefined, mode: 'select' | 'end') => {
  if (!el) return;
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
    el.focus();
    return;
  }
  el.focus();
  if (mode === 'select') {
    if (el instanceof HTMLInputElement && ['text', 'search', 'tel', 'url', 'password'].includes(el.type)) {
      el.select();
    } else if (el instanceof HTMLTextAreaElement) {
      el.select();
    }
  } else {
    const len = el.value.length;
    try { el.setSelectionRange(len, len); } catch { /* тип без selection */ }
  }
};

const focusSiblingField = (from: HTMLElement, dir: 1 | -1, mode: 'select' | 'end') => {
  const fields = collectFields();
  const idx = fields.indexOf(from);
  if (idx === -1) return;
  focusField(fields[idx + dir], mode);
};

export const focusNextField = (from: HTMLElement) => {
  window.setTimeout(() => focusSiblingField(from, 1, 'select'), 0);
};

// Подтверждение в поле исполнителей: переход к следующему полю, а если его нет
// (в замене масла «Исполнители» — последнее поле формы) — blur, чтобы
// контролируемый open селектора закрылся (onDropdownVisibleChange)
const confirmAndLeave = (from: HTMLElement) => {
  window.setTimeout(() => {
    const fields = collectFields();
    const idx = fields.indexOf(from);
    const next = idx >= 0 ? fields[idx + 1] : undefined;
    if (next) focusField(next, 'select');
    else from.blur();
  }, 0);
};

// Единая логика Enter/стрелки в multiple-Select исполнителей (ЗН №21 и замена масла №53):
// Enter на НЕвыбранной опции — antd выбирает (глобальный enterAsTab глушится флагом),
// Enter на уже выбранной или стрелка вправо — подтверждение: переход к следующему полю
// (stopPropagation, чтобы antd не снял выбор). Индекс активной опции — из
// aria-activedescendant поискового input'а ({id}_list_{индекс}).
export const executorSelectKeyDown = (
  e: { key: string; target: EventTarget | null; nativeEvent: Event; stopPropagation: () => void },
  options: { value: string }[],
  selectedIds: string[],
) => {
  if (e.key !== 'Enter' && e.key !== 'ArrowRight') return;
  const input = e.target as HTMLInputElement;
  if (e.key === 'ArrowRight') {
    e.stopPropagation();
    confirmAndLeave(input);
    return;
  }
  const activeId = input.getAttribute('aria-activedescendant');
  if (activeId) {
    const idx = Number(activeId.split('_list_')[1]);
    const opt = options[idx];
    if (opt && selectedIds.includes(opt.value)) {
      e.stopPropagation();
      confirmAndLeave(input);
      return;
    }
  }
  (e.nativeEvent as unknown as Record<string, unknown>)[SKIP_FLAG] = true;
};

export const enterAsTab = (e: KeyboardEvent) => {
  if (e.key !== 'Enter') return;
  if ((e as unknown as Record<string, unknown>)[SKIP_FLAG]) return;
  const t = e.target;
  if (!(t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement)) return;
  if (isSkippable(t)) return;
  if (t.closest(PORTAL_SELECTOR)) return;
  focusNextField(t);
};

export const arrowFieldNav = (e: KeyboardEvent) => {
  if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
  const t = e.target;
  // Быстрые кнопки услуг (rev. №56): стрелка вправо — следующее поле/кнопка,
  // влево — предыдущее; позиции курсора у кнопки нет
  if (t instanceof HTMLButtonElement) {
    if (isSkippable(t) || t.closest(PORTAL_SELECTOR)) return;
    e.preventDefault();
    e.stopPropagation();
    focusSiblingField(t, e.key === 'ArrowRight' ? 1 : -1, e.key === 'ArrowRight' ? 'select' : 'end');
    return;
  }
  if (!(t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement)) return;
  if (t.classList.contains(SELECT_SEARCH_CLASS)) {
    if (t.getAttribute('aria-expanded') === 'true') return;
    if (e.key === 'ArrowLeft' && t.closest('.ant-select')?.classList.contains('ant-select-multiple')) return;
  }
  if (isSkippable(t)) return;
  if (t.closest(PORTAL_SELECTOR)) return;
  const len = t.value.length;
  const atEnd = t.selectionStart === len && t.selectionEnd === len;
  const atStart = t.selectionStart === 0 && t.selectionEnd === 0;
  if (e.key === 'ArrowRight' && atEnd) {
    e.preventDefault();
    e.stopPropagation();
    focusSiblingField(t, 1, 'select');
  } else if (e.key === 'ArrowLeft' && atStart) {
    e.preventDefault();
    e.stopPropagation();
    focusSiblingField(t, -1, 'end');
  }
};
